import type { IPOI, IPoiPendingImage, IPoiTranslation } from '@/entities/poi'
import type { PoiEditSection, PoiValidationError } from '@/features/poi/poi-form-model'
import type {
  PoiBasicInfoState,
  PoiLocationState,
  UpdatePoiPayload,
} from '@/shared/api/types/poi-types'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreatePoiMutation, useUpdatePoiMutation } from '@/entities/poi'
import {
  buildBasicInfoUpdatePayload,
  buildCreatePoiPayload,
  buildLocationUpdatePayload,
  hasUploadingImages,
  normalizePoiTranslations,
  validatePoiBasicInfo,
  validatePoiImages,
  validatePoiSubmission,
  validatePoiTranslations,
} from '@/features/poi/poi-form-model'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { generatePath } from '@/shared/libs/generate-path'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'

interface UsePoiFormActionsParams {
  tenantId: string
  poiId: string | undefined
  isEdit: boolean
  basicInfo: PoiBasicInfoState
  location: PoiLocationState
  translations: IPoiTranslation[]
  images: IPoiPendingImage[]
  bannerUrls: string[]
  tenantDestinationId: string
  isActive: boolean
  clearCurrentJobs: () => void
  /** Lets the next route change through the unsaved-changes guard (after a successful save). */
  allowNextNavigation: () => void
  commitBaseline: {
    translations: (saved: IPoiTranslation[]) => void
    location: () => void
    basicInfo: () => void
    media: () => void
  }
}

/** Create flow (whole form at once) and the independent per-section saves of the edit page. */
export function usePoiFormActions({
  tenantId,
  poiId,
  isEdit,
  basicInfo,
  location,
  translations,
  images,
  bannerUrls,
  tenantDestinationId,
  isActive,
  clearCurrentJobs,
  allowNextNavigation,
  commitBaseline,
}: UsePoiFormActionsParams) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const createMutation = useCreatePoiMutation()
  const updateMutation = useUpdatePoiMutation()
  const [savingSection, setSavingSection] = useState<PoiEditSection | null>(null)

  const listPath = scopedAdminPath(generatePath('/tenant/:tenantId/pois', { tenantId }))
  const createPath = scopedAdminPath(generatePath('/tenant/:tenantId/pois/create', { tenantId }))

  function showValidationError(error: PoiValidationError) {
    showToast.error(t(error.key))
  }

  function navigateToList() {
    void navigate({ href: listPath })
  }

  /** Sends the admin to create a related record and brings them back to this exact form afterwards. */
  function navigateAndReturn(targetPath: string) {
    const returnTo =
      typeof window === 'undefined'
        ? createPath
        : `${window.location.pathname}${window.location.search}`
    allowNextNavigation()
    void navigate({ href: `${targetPath}?returnTo=${encodeURIComponent(returnTo)}` })
  }

  function handleCreateCategoryNavigation() {
    navigateAndReturn(
      scopedAdminPath(generatePath('/tenant/:tenantId/poi-categories/create', { tenantId })),
    )
  }

  function handleCreateDestinationNavigation() {
    navigateAndReturn(scopedAdminPath(generatePath('/tenant/:tenantId/destinations', { tenantId })))
  }

  /**
   * Create flow: validate the whole form, build the API payload, create the POI and go back to the list.
   * (Editing never goes through here — each edit section has its own save below.)
   */
  async function handleSubmit() {
    if (isEdit) return

    const normalizedTranslations = normalizePoiTranslations(translations)
    const validationError = validatePoiSubmission({
      basicInfo,
      tenantDestinationId,
      images,
      translations: normalizedTranslations,
    })
    const { type } = basicInfo
    if (validationError || !type) {
      // `type` is non-empty whenever validation passed; the check only narrows it for the payload builder.
      if (validationError) showValidationError(validationError)
      return
    }

    try {
      await createMutation.mutateAsync(
        buildCreatePoiPayload({
          type,
          basicInfo,
          location,
          tenantDestinationId,
          translations: normalizedTranslations,
          bannerUrls,
          tenantId,
          isActive,
        }),
      )
      // * VI: Tạo thành công thì xóa draft để lần mở sau không restore dữ liệu cũ. | EN: Clear the draft after a successful create so stale values are not restored next time.
      clearCurrentJobs()
      showToast.success(t('poi.createSuccess'))
      allowNextNavigation()
      await navigate({ href: listPath })
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  /** Shared wrapper of the edit-page section saves: loading flag and toasts; resolves to the updated POI, or `null` on failure. */
  async function saveEditSection(
    section: PoiEditSection,
    payload: UpdatePoiPayload,
  ): Promise<IPOI | null> {
    if (!poiId) return null
    try {
      setSavingSection(section)
      const response = await updateMutation.mutateAsync({ id: poiId, payload })
      showToast.success(t('poi.updateSuccess'))
      return response.data
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
      return null
    } finally {
      setSavingSection(null)
    }
  }

  // * VI: Chỉ cập nhật baseline khi API thành công, để dirty-check luôn phản ánh đúng dữ liệu đã lưu. | EN: Baselines are updated only after the API succeeded, so dirty checks always reflect what is really saved.
  async function handleSaveTranslations() {
    const normalizedTranslations = normalizePoiTranslations(translations)
    const validationError = validatePoiTranslations(normalizedTranslations)
    if (validationError) {
      showValidationError(validationError)
      return
    }
    const updatedPoi = await saveEditSection('translations', {
      localizedData: normalizedTranslations,
    })
    if (updatedPoi) {
      commitBaseline.translations(updatedPoi.localizedData)
    }
  }

  async function handleSaveLocation() {
    if (await saveEditSection('location', buildLocationUpdatePayload(location))) {
      commitBaseline.location()
    }
  }

  async function handleSaveBasicInfo() {
    const validationError = validatePoiBasicInfo(basicInfo, tenantDestinationId)
    const { type } = basicInfo
    if (validationError || !type) {
      if (validationError) showValidationError(validationError)
      return
    }
    const payload = buildBasicInfoUpdatePayload(basicInfo, type, tenantDestinationId)
    if (await saveEditSection('basicInfo', payload)) {
      commitBaseline.basicInfo()
    }
  }

  async function handleSaveMedia() {
    const validationError = validatePoiImages(images)
    if (validationError) {
      showValidationError(validationError)
      return
    }
    if (await saveEditSection('media', { banner: bannerUrls })) {
      commitBaseline.media()
    }
  }

  const isUploadingImages = hasUploadingImages(images)

  return {
    savingSection,
    isUpdating: updateMutation.isPending,
    isBusy: updateMutation.isPending || savingSection !== null,
    isUploadingImages,
    isSaving: createMutation.isPending || updateMutation.isPending || isUploadingImages,
    navigateToList,
    handleCreateCategoryNavigation,
    handleCreateDestinationNavigation,
    handleSubmit,
    handleSaveTranslations,
    handleSaveLocation,
    handleSaveBasicInfo,
    handleSaveMedia,
  }
}
